import { requestCodexRuntimeReadiness } from "./codexRuntimeReadinessRequest";
import { continueCodexRuntimeReadiness } from "./codexRuntimeReadinessContinuation";

export const CODEX_READINESS_BLOCK_CODES = [
  "CODEX_RECONNECT_REQUIRED",
  "CODEX_ACCOUNT_IDENTITY_MISMATCH",
  "RUNTIME_AUTHORITY_UPGRADE_BLOCKED",
  "RUNTIME_EXACT_AUTHORITY_REQUIRED",
  "RUNTIME_AUTH_REQUIRED",
  "RUNTIME_AUTH_FORBIDDEN",
  "CMMD_AUTH_REQUIRED",
  "CMMD_PERMISSION_DENIED",
  "CMMD_RUNTIME_CONNECT_TIMEOUT",
  "CMMD_RUNTIME_AUTHORITY_UPGRADE_TIMEOUT",
  "CMMD_RUNTIME_AUTHORITY_UPGRADE_STATUS_TIMEOUT",
  "CMMD_RUNTIME_RECONNECT_TIMEOUT",
  "CMMD_RUNTIME_CREDENTIAL_TIMEOUT",
  "CMMD_RUNTIME_UNAVAILABLE",
  "CMMD_UNAVAILABLE",
  "INVALID_RUNTIME_REQUEST",
  "INVALID_MUTATION_ORIGIN",
  "RUNTIME_DELIVERY_RECEIPT_INVALID",
  "RUNTIME_READINESS_UNAVAILABLE",
  "READINESS_RESPONSE_INVALID",
  "READINESS_UNAVAILABLE",
] as const;

export type CodexReadinessBlockCode = (typeof CODEX_READINESS_BLOCK_CODES)[number];

export type CodexRuntimeReadiness =
  | { readonly status: "not_checked" }
  | { readonly status: "checking" }
  | {
      readonly status: "ready";
      readonly accountEmail: string | null;
      readonly planType: string | null;
      readonly model: "gpt-5.6-sol";
      readonly credentialEpoch: number;
    }
  | {
      readonly status: "blocked";
      readonly code: CodexReadinessBlockCode;
      readonly details?: CodexRuntimeReadinessFailureDetails;
    };

export interface CodexRuntimeReadinessFailureDetails {
  readonly phase: "connect" | "authority_upgrade" | "reconnect" | "credential";
  readonly upstreamStatus: number | null;
}

type ReadinessResult = Exclude<
  CodexRuntimeReadiness,
  { readonly status: "not_checked" | "checking" }
>;
type UnknownRecord = Record<string, unknown>;

const PROVIDER_ACCOUNT_IDENTITY = /^pai_v1_[A-Za-z0-9_-]{43}$/;
const RFC3339 =
  /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;
const BLOCK_CODES = new Set<string>(CODEX_READINESS_BLOCK_CODES);
const READINESS_FAILURE_DETAIL_CODES = new Set<CodexReadinessBlockCode>([
  "CMMD_RUNTIME_CONNECT_TIMEOUT",
  "CMMD_RUNTIME_AUTHORITY_UPGRADE_TIMEOUT",
  "CMMD_RUNTIME_RECONNECT_TIMEOUT",
  "CMMD_RUNTIME_CREDENTIAL_TIMEOUT",
  "CMMD_RUNTIME_UNAVAILABLE",
]);
const READINESS_FAILURE_DETAIL_KEYS = new Set(["phase", "upstreamStatus"]);
const READINESS_FAILURE_PHASES = new Set<CodexRuntimeReadinessFailureDetails["phase"]>([
  "connect",
  "authority_upgrade",
  "reconnect",
  "credential",
]);
const ENVELOPE_KEYS = new Set([
  "ready",
  "provider",
  "model",
  "runtime",
  "account",
  "deliveryReceipt",
]);
const RUNTIME_KEYS = new Set(["runtimeId", "machineId", "credentialEpoch"]);
const ACCOUNT_KEYS = new Set(["accountEmail", "planType", "providerAccountIdentity", "updatedAt"]);
const RECEIPT_KEYS = new Set([
  "receiptId",
  "runtimeId",
  "machineId",
  "credentialEpoch",
  "identity",
  "issuedAt",
  "expiresAt",
]);
const IDENTITY_KEYS = new Set(["kind", "provider", "providerAccountIdentity"]);

function asRecord(value: unknown): UnknownRecord | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || isNonEmptyString(value);
}

function isDateTime(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = RFC3339.exec(value);
  if (!match || !Number.isFinite(Date.parse(value))) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function hasExactKeys(value: UnknownRecord, keys: ReadonlySet<string>): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.size && actual.every((key) => keys.has(key));
}

function isCredentialEpoch(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

interface ParsedRuntime {
  readonly runtimeId: string;
  readonly machineId: string;
  readonly credentialEpoch: number;
}

interface ParsedAccount {
  readonly accountEmail: string | null;
  readonly planType: string | null;
  readonly providerAccountIdentity: string;
}

function parseRuntime(value: unknown, expectedRuntimeId: string): ParsedRuntime | null {
  const runtime = asRecord(value);
  if (!runtime || !hasExactKeys(runtime, RUNTIME_KEYS) || runtime.runtimeId !== expectedRuntimeId) {
    return null;
  }
  if (!isNonEmptyString(runtime.machineId) || !isCredentialEpoch(runtime.credentialEpoch)) {
    return null;
  }
  return {
    runtimeId: expectedRuntimeId,
    machineId: runtime.machineId,
    credentialEpoch: runtime.credentialEpoch,
  };
}

function parseAccount(value: unknown): ParsedAccount | null {
  const account = asRecord(value);
  if (
    !account ||
    !hasExactKeys(account, ACCOUNT_KEYS) ||
    !isNullableString(account.accountEmail) ||
    !isNullableString(account.planType)
  ) {
    return null;
  }
  if (
    typeof account.providerAccountIdentity !== "string" ||
    !PROVIDER_ACCOUNT_IDENTITY.test(account.providerAccountIdentity) ||
    !isDateTime(account.updatedAt)
  ) {
    return null;
  }
  return {
    accountEmail: account.accountEmail,
    planType: account.planType,
    providerAccountIdentity: account.providerAccountIdentity,
  };
}

function receiptTimingIsValid(receipt: UnknownRecord): boolean {
  if (
    typeof receipt.receiptId !== "string" ||
    receipt.receiptId.length === 0 ||
    receipt.receiptId.length > 256 ||
    !isDateTime(receipt.issuedAt)
  ) {
    return false;
  }
  return isDateTime(receipt.expiresAt) && Date.parse(receipt.expiresAt) > Date.now();
}

function receiptAuthorityMatches(
  receipt: UnknownRecord,
  runtime: ParsedRuntime,
  account: ParsedAccount,
): boolean {
  const identity = asRecord(receipt.identity);
  if (!identity || !hasExactKeys(receipt, RECEIPT_KEYS) || !hasExactKeys(identity, IDENTITY_KEYS)) {
    return false;
  }
  const runtimeMatches =
    receipt.runtimeId === runtime.runtimeId &&
    receipt.machineId === runtime.machineId &&
    receipt.credentialEpoch === runtime.credentialEpoch;
  const identityMatches =
    identity.kind === "provider_account" &&
    identity.provider === "codex" &&
    identity.providerAccountIdentity === account.providerAccountIdentity;
  return runtimeMatches && identityMatches;
}

function isReadyEnvelope(body: UnknownRecord): boolean {
  return body.ready === true && body.provider === "codex" && body.model === "gpt-5.6-sol";
}

function parseReadyEnvelope(value: unknown): UnknownRecord | null {
  const body = asRecord(value);
  if (!body || !hasExactKeys(body, ENVELOPE_KEYS) || !isReadyEnvelope(body)) return null;
  return body;
}

interface ParsedReadyContext {
  readonly runtime: ParsedRuntime;
  readonly account: ParsedAccount;
}

function parseReadyContext(
  body: UnknownRecord,
  expectedRuntimeId: string,
): ParsedReadyContext | null {
  const runtime = parseRuntime(body.runtime, expectedRuntimeId);
  const account = parseAccount(body.account);
  const receipt = asRecord(body.deliveryReceipt);
  if (!runtime || !account || !receipt) return null;
  if (!receiptTimingIsValid(receipt) || !receiptAuthorityMatches(receipt, runtime, account)) {
    return null;
  }
  return { runtime, account };
}

function parseReadyResult(value: unknown, expectedRuntimeId: string): ReadinessResult | null {
  const body = parseReadyEnvelope(value);
  if (!body) return null;
  const context = parseReadyContext(body, expectedRuntimeId);
  if (!context) return null;

  return {
    status: "ready",
    accountEmail: context.account.accountEmail,
    planType: context.account.planType,
    model: "gpt-5.6-sol",
    credentialEpoch: context.runtime.credentialEpoch,
  };
}

function parseBlockCode(value: unknown): CodexReadinessBlockCode {
  const code = asRecord(value)?.code;
  return typeof code === "string" && BLOCK_CODES.has(code as CodexReadinessBlockCode)
    ? (code as CodexReadinessBlockCode)
    : "READINESS_UNAVAILABLE";
}

function isReadinessFailurePhase(
  value: unknown,
): value is CodexRuntimeReadinessFailureDetails["phase"] {
  return (
    typeof value === "string" &&
    READINESS_FAILURE_PHASES.has(value as CodexRuntimeReadinessFailureDetails["phase"])
  );
}

function isUpstreamStatus(value: unknown): value is number | null {
  return (
    value === null ||
    (typeof value === "number" && Number.isSafeInteger(value) && value >= 100 && value <= 599)
  );
}

function parseFailureDetails(value: unknown): CodexRuntimeReadinessFailureDetails | null {
  const details = asRecord(value);
  if (!details || !hasExactKeys(details, READINESS_FAILURE_DETAIL_KEYS)) return null;
  const phase = details.phase;
  const upstreamStatus = details.upstreamStatus;
  if (!isReadinessFailurePhase(phase) || !isUpstreamStatus(upstreamStatus)) {
    return null;
  }
  return { phase, upstreamStatus };
}

function parseBlockedResult(value: unknown): ReadinessResult {
  const code = parseBlockCode(value);
  const details = READINESS_FAILURE_DETAIL_CODES.has(code)
    ? parseFailureDetails(asRecord(value)?.details)
    : null;
  return {
    status: "blocked",
    code,
    ...(details ? { details } : {}),
  };
}

export async function fetchCodexRuntimeReadiness(input: {
  readonly runtimeId: string;
  readonly organizationId: string;
  readonly signal?: AbortSignal;
}): Promise<ReadinessResult> {
  const runtimeId = input.runtimeId.trim();
  const organizationId = input.organizationId.trim();
  if (!runtimeId || !/^[1-9]\d*$/.test(organizationId)) {
    return { status: "blocked", code: "INVALID_RUNTIME_REQUEST" };
  }
  try {
    const response = await requestCodexRuntimeReadiness({
      runtimeId,
      organizationId,
      ...(input.signal ? { signal: input.signal } : {}),
    });
    if (!response.ok) return parseBlockedResult(response.body);
    return await continueCodexRuntimeReadiness({
      initial: response,
      runtimeId,
      parseReady: parseReadyResult,
      parseBlocked: parseBlockCode,
      ...(input.signal ? { signal: input.signal } : {}),
    });
  } catch {
    return { status: "blocked", code: "READINESS_UNAVAILABLE" };
  }
}
